/*
[GNU Affero General Public License (AGPL)](https://www.gnu.org/licenses/agpl-3.0.html)

VeloceUI - A UI component library for Angular v17+ made with care by Colibryx Srl in Italy.
Copyright (C) 2025 Colibryx S.r.l.

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as published by
the Free Software Foundation, either version 3 of the License, or
(at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

This license applies to any accompanying files located directly in the same directory.

For any inquiry, please contact Colibryx S.r.l. at info@colibryx.com.
*/

import { CommonModule } from "@angular/common";
import {
	ChangeDetectionStrategy,
	Component,
	type ElementRef,
	HostListener,
	Injector,
	type OnDestroy,
	type OnInit,
	ViewContainerRef,
	inject,
	input,
	output,
	viewChild
} from "@angular/core";
import { ThemeService } from "@app/@services/generic/theme.service";
import { takeUntil } from "rxjs";
import { DialogGeneric } from "../dialog.abstract";
import { BACKDROP_ANIMATIONS, DIALOG_ANIMATIONS } from "../dialog.animations";
import type { CbDialogConfig, CbDialogRef } from "../dialog.class";
import { DialogService } from "../dialog.service";

@Component({
	selector: "cb-dialog",
	templateUrl: "./dialog.component.html",
	styleUrl: "./dialog.component.scss",
	imports: [CommonModule],
	animations: [...BACKDROP_ANIMATIONS, ...DIALOG_ANIMATIONS],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class DialogComponent
	extends DialogGeneric
	implements OnInit, OnDestroy {
	protected override dialogService: DialogService = inject(DialogService);

	public readonly dialogRef = input.required<CbDialogRef>();
	public readonly dialogConfig = input.required<CbDialogConfig>();

	protected readonly injector: Injector = inject(Injector);
	protected readonly vcr: ViewContainerRef = inject(ViewContainerRef);
	protected readonly themeService: ThemeService = inject(ThemeService);

	/**
	 * The dialog wrapper element.
	 */
	readonly dialogWrapper = viewChild.required<ElementRef<any>>("DialogWrapper");

	public onDestroy = output<void>();

	@HostListener("document:keydown", ["$event"])
	handleKeyboardEvent(event: KeyboardEvent) {
		if (event.key === "Escape") {
			this.dialogRef().close();
		}
	}

	ngOnInit(): void {
		this.init();

		this.onDestroy$.pipe(takeUntil(this.destroy$)).subscribe(() => {
			this.onDestroy.emit();
		});
	}

	ngOnDestroy(): void {
		this.destroy();
	}
}
